import {Mutable} from 'utility-types';
import {
    HibuDocumentDuplicates,
    HibuHighlightFields,
    HibuHit,
    HibuHitDoc,
    HibuSearchRequestSort,
    HibuSearchRequestSpellcheckParams,
    HibuTag,
    HibuTags,
    Highlights,
    ProdDocument,
    ProductDocument,
    SearchRequest,
    SearchSortType,
    SortType,
    TagMap,
    TagMapEntry,
} from './search-types';
import {
    generateRandomSortSeed,
    handleSearchError,
    isSearchInBookmarks,
    isSpellcheckEnabled,
    localStorageSupported
} from '../utils/utils';
import {SearchPageTypeState} from './duck/search-types';
import {Language} from '../i18nTypes';
import {SearchPageType} from './search-config';
import {getFileDownloadApiUrl, getFilePreviewApiUrl, SEARCH_BATCH_SIZE} from '../services/search-service';
import {Dispatch, GetState} from '../app-types';
import {getCurrentLanguage} from '../i18n-service';
import {getDefaultSortType} from './search-hooks';
import {AbortCategory, fetchFile} from '../services/fetcher';
import {HibuFacetFilter} from '../facets/facet.types';

export const SEARCH_DOCUMENTS_ENDPOINT = 'searchDocuments';
export const SEARCH_FACETS_ENDPOINT = 'searchFacets';
export const CLOSE_POINT_IN_TIME_ENDPOINT = 'closePointInTime';
export const BOOKMARKS_API_ENDPOINT = 'bookmarks';
export const HIGHLIGHT_API_ENDPOINT = 'highlightDocument';
export const HISTORY_API_ENDPOINT = 'searchHistory';
export const SAVED_SEARCH_API_ENDPOINT = 'savedSearch';
export const FETCH_DOWNLOAD_FILE_API_ENDPOINT = 'downloadFile';
export const FETCH_PREVIEW_FILE_API_ENDPOINT = 'previewFile';
export const FETCH_PREVIEW_DOCUMENT_API_ENDPOINT = 'previewDocument';
export const HTML_EXPORT = 'htmlexport';
export const JSON_EXPORT = 'jsonexport';

export function getSearchRequest(
    getState: GetState,
    query?: string,
    isUsingAutoCorrection?: boolean,
    docOffset?: number,
): SearchRequest {
    const searchPageType: SearchPageType = SearchPageType.BASIC;
    const state = getState();
    const container = state.settings.containerName;
    const language = getCurrentLanguage();
    if (!query) {
        query = state.search[searchPageType].query;
    }
    docOffset ??= state.search[searchPageType].docOffset;
    const pointInTime = state.search[searchPageType].pointInTime;
    const filters: HibuFacetFilter = state.filter.selectedFilters;
    const currentSortType: SortType = state.search[searchPageType].currentSortType;
    const defaultSortType: SortType | undefined = getDefaultSortType(state);
    const sort: HibuSearchRequestSort = getSort(query, currentSortType, defaultSortType);
    const spellcheckEnabled = isSpellcheckEnabled(state.search.BASIC.sources);

    const showFieldSpecificSearch = state.settings.fieldSpecificSearch;
    const queryFields = showFieldSpecificSearch ? state.search[searchPageType].queryFields : undefined;

    return {
        container: container,
        displayLanguage: language,
        query: query,
        from: docOffset,
        size: SEARCH_BATCH_SIZE,
        pointInTime: pointInTime,
        filters: filters,
        sort: sort,
        spellcheckEnabled: spellcheckEnabled,
        spellcheckParams: getSpellcheckParams(isUsingAutoCorrection),
        queryFields: queryFields,
        isSearchInBookmarks: isSearchInBookmarks(state.search.BASIC.sources)
    };
}

export function getEmptySearchRequest(containerName: string): SearchRequest {
    const language = getCurrentLanguage();
    const sort: HibuSearchRequestSort = {
        seed: getRandomSeed(),
        field: SearchSortType.RANDOM.field,
        order: SearchSortType.RANDOM.order,
    };
    return {
        container: containerName,
        displayLanguage: language,
        query: '',
        from: 0,
        size: SEARCH_BATCH_SIZE,
        pointInTime: undefined,
        filters: {},
        sort: sort,
        spellcheckEnabled: true,
        spellcheckParams: getSpellcheckParams(false),
        queryFields: undefined,
        isSearchInBookmarks: false
    };
}

export const isDefaultSortNeeded = (query: string, sortField: string): boolean => {
    return query === '' && sortField.includes(SearchSortType.RELEVANCE.field);
};

const getRandomSeed = (): string => {
    let randomSortSeed: string | null = null;
    if (localStorageSupported()) {
        randomSortSeed = localStorage.getItem('randomSortSeed');
    }
    randomSortSeed ??= generateRandomSortSeed();
    return randomSortSeed;
};

/*
behavior (note: the default for currentSortType is relevance):
* empty search + currentSortType set to relevance (score) → default sort (first in list) under the hood but ui shows relevance
* query term not empty + currentSortType set to relevance → relevance
* currentSortType set (from ui or url) to sth else than relevance → as selected resp. set from url
*/
export const getSort = (
    query: string,
    currentSortType: SortType,
    defaultSortType: SortType | undefined,
): HibuSearchRequestSort => {
    let sortType: SortType;
    if (defaultSortType && isDefaultSortNeeded(query, currentSortType.field)) {
        sortType = defaultSortType;
    } else {
        sortType = currentSortType;
    }
    return {
        seed: sortType.field.includes(SearchSortType.RANDOM.field) ? getRandomSeed() : undefined,
        field: sortType.field,
        order: sortType.order,
    };
};

export const getActualQueryString = (searchState: SearchPageTypeState): string => {
    return searchState.resultFor ? searchState.resultFor : searchState.query;
};

export const isBlacklistedForAutoCorrection = (query: string): boolean => {
    const blacklistForAutoCorrection = ['PMEA'];
    return blacklistForAutoCorrection.some((entry) => entry.localeCompare(query, [], {sensitivity: 'accent'}) === 0);
};

export function getSpellcheckParams(isUsingAutoCorrection?: boolean): HibuSearchRequestSpellcheckParams {
    return isUsingAutoCorrection
        ? {
            spellcheckSuggestionMinDocFreq: 1,
            spellcheckSuggestionMinDocFreqGain: 1,
            autoCorrectionMinDocFreq: 5,
            autoCorrectionMinDocFreqGain: 5,
            autoCorrectionOriginalQueryMaxDocFreq: 1,
        }
        : {
            spellcheckSuggestionMinDocFreq: 1,
            spellcheckSuggestionMinDocFreqGain: 1,
        };
}

export const mapHighlights = (highlightFields: HibuHighlightFields[]): Mutable<Highlights> => {
    const highlights: Mutable<Highlights> = {};
    if (highlightFields) {
        for (const highlightField of highlightFields) {
            if (highlightField.key === 'contentSource') {
                highlights.contentSource = highlightField.fragments;
            }
            if (highlightField.key === 'title') {
                highlights.title = highlightField.fragments;
            }
            if (highlightField.key === 'content') {
                highlights.content = highlightField.fragments;
            }
        }
    }
    return highlights;
};

export const mapTag = (tag: TagMapEntry, currentLanguage: Language, documentLanguage: Language): HibuTag => {
    return {
        id: tag.tagId,
        label: tag.labels?.[currentLanguage] || tag.labels?.[documentLanguage],
        spans: tag.spans,
        extra: {...tag.extra, aggregated: tag.extra?.aggregationName_tokenized_string !== undefined},
    };
};

export const mapTagsForDocType = (
    tagTypeKey: string,
    currentLanguage: Language,
    tags: TagMap,
    documentLanguage: Language,
): HibuTag[] => {
    return tags[tagTypeKey].map((it) => {
        return mapTag(it, currentLanguage, documentLanguage);
    });
};

export const mapTags = (tags: TagMap, currentLanguage: Language, documentLanguage: Language): HibuTags => {
    const hibuTags: HibuTags = {};
    if (tags) {
        for (const prop in tags) {
            if (Object.hasOwn(tags, prop)) {
                hibuTags[prop] = mapTagsForDocType(prop, currentLanguage, tags, documentLanguage);
            }
        }
    }
    return hibuTags;
};

export function mapDocument(
    doc: HibuHitDoc,
    highlightFields: HibuHighlightFields[],
    duplicates: HibuDocumentDuplicates[],
) {
    const tags = doc.tags;
    // @ts-ignore FIXME
    delete doc.tags;

    const mappedDoc: Partial<ProductDocument> = {
        ...doc,
        highlights: mapHighlights(highlightFields),
        hibuTags: mapTags(tags, getCurrentLanguage(), doc.language as Language),
        duplicates: duplicates,
        // @ts-ignore FIXME
        expanded: false, // todo: needed? since they don't exist on the ProductDocument type
    };
    return mappedDoc as ProductDocument;
}

/**
 * Receives the unformatted JSON object from services.
 * It will be reformatted to be used in the product details component.
 *
 * @param docs an array of unformatted products
 * @returns an array of formatted products
 */
export const formatHits = (docs: HibuHit[]): ProductDocument[] => {
    if (!docs) {
        return [];
    }

    return docs.map(({doc, highlightFields}: HibuHit) => {
        return mapDocument(doc, highlightFields, doc.duplicates);
    });
};

export const downloadFile = async (doc: ProdDocument, dispatch: Dispatch) => {
    const url = extractDownloadUrl(doc, true);
    if (url) {
        fetchFile(url, JSON.stringify({link: doc.metadata.downloadLink_string}), AbortCategory.DOWNLOAD)
            .then((response: Blob) => {
                const downloadUrl = window.URL.createObjectURL(response);

                // create temporary <a>-tag
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = doc.title || 'download';
                link.click();

                // free memory
                link.remove();
                window.URL.revokeObjectURL(downloadUrl);
            }).catch((error: any) => {
            return handleSearchError(error, dispatch);
            }
        );
    }
};

/**
 * When getOriginalFile is true, then the original file, that is not indexed, is fetched.
 * If not, then the indexed pdf with the ocr-result is fetched.
 * Return undefined when no link is present at all.
 */
export function extractDownloadUrl(
    doc: ProductDocument,
    getOriginalFile: boolean,
): string | undefined {
    if (doc.metadata.downloadLink_string) {
        if (getOriginalFile) {
            return getFileDownloadApiUrl(SearchPageType.BASIC);
        } else {
            return getFilePreviewApiUrl(SearchPageType.BASIC);
        }
    }
}

/**
 * Constructs and returns a param string from key and value
 * @param uriKey the key of the param
 * @param uri the value of the param
 * @param encode whether the value should be encoded
 */
export function parseURI(uriKey: string, uri: string, encode = true): string {
    return `${uriKey}=${encode ? encodeURIComponent(uri) : uri}`;
}
